import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
    IconBriefcase, IconPlus, IconTrendingUp, 
    IconActivity, IconSave, IconX, IconRefresh, IconList, IconTrash
} from '../components/Icons';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config';

// --- INTERFACES (Unchanged) ---
interface Book { id: string; name: string; description: string; }
interface Position { ticker: string; shares: number; avg_cost: number; current_price: number; market_value: number; total_pnl: number; pnl_percent: number; }
interface Transaction { id: string; ticker: string; action: string; amount: number; price: number; timestamp: string; }

const PortfolioView: React.FC = () => {
    const { currentUser } = useAuth();
    
    // --- STATE ---
    const [books, setBooks] = useState<Book[]>([]);
    
    // 1. Initialize from LocalStorage to prevent "Blank Screen" on return
    const [selectedBookId, setSelectedBookId] = useState<string | null>(() => {
        return localStorage.getItem('aiq_last_book_id') || null;
    });

    const [activeTab, setActiveTab] = useState<'POSITIONS' | 'TRANSACTIONS'>('POSITIONS');
    const [positions, setPositions] = useState<Position[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [summary, setSummary] = useState<any>(null);
    
    // UI State
    const [isBooksLoading, setIsBooksLoading] = useState(true); // Loading the list
    const [isDataRefreshing, setIsDataRefreshing] = useState(false); // Updating the numbers
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTradeModal, setShowTradeModal] = useState(false);
    const [newBookName, setNewBookName] = useState('');
    const [tradeTicket, setTradeTicket] = useState({ ticker: '', action: 'BUY', amount: '', price: '' });

    // --- 1. LOAD BOOKS ---
    useEffect(() => {
        const fetchBooks = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/portfolios`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.portfolios) {
                    setBooks(data.portfolios);
                    // If no book selected yet (and not in localstorage), select first
                    if (!selectedBookId && data.portfolios.length > 0) {
                        handleSelectBook(data.portfolios[0].id);
                    }
                }
            } catch (e) {
                console.error("Failed to load books", e);
            } finally {
                setIsBooksLoading(false);
            }
        };
        fetchBooks();
    }, [currentUser]);

    // Helper to switch books and persist choice
    const handleSelectBook = (id: string) => {
        setSelectedBookId(id);
        localStorage.setItem('aiq_last_book_id', id);
        // Reset view data temporarily or keep old? Better to clear to avoid confusion
        setSummary(null); 
        setPositions([]);
        setTransactions([]);
    };

    // --- 2. LOAD DETAILS (The Smart Fetch) ---
    const fetchBookDetails = async (forceRecalculate = false) => {
        if (!currentUser || !selectedBookId) return;
        
        // Don't block UI if we are just refreshing
        setIsDataRefreshing(true);
        
        try {
            const token = await currentUser.getIdToken();
            
            // A. Fetch Summary
            // If forceRecalculate is true, we hit the POST endpoint to force update.
            // Otherwise, we hit GET to retrieve cached data instantly.
            let summaryEndpoint = `${API_BASE_URL}/portfolios/${selectedBookId}/summary`;
            let method = 'GET';
            
            if (forceRecalculate) {
                summaryEndpoint = `${API_BASE_URL}/portfolios/${selectedBookId}/recalculate_summary`;
                method = 'POST';
                toast.success("Recalculating P&L...");
            }

            const resSummary = await fetch(summaryEndpoint, {
                method: method,
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const responseJson = await resSummary.json();
            // Handle different response structures (GET returns direct obj, POST returns {status, data})
            const dataSummary = forceRecalculate ? responseJson.data : responseJson;

            if (dataSummary) {
                setSummary(dataSummary);
                const rawPositions = dataSummary.pnl_by_ticker || [];
                const mappedPositions = rawPositions
                    .filter((p: any) => p.shares_held > 0)
                    .map((p: any) => ({
                        ticker: p.ticker,
                        shares: p.shares_held,
                        avg_cost: p.avg_cost,
                        current_price: p.current_price,
                        market_value: p.market_value,
                        total_pnl: p.total_pnl,
                        pnl_percent: p.pnl_percentage
                    }));
                setPositions(mappedPositions);
            }

            // B. Fetch Transactions (Always fetch fresh blotter)
            const resTx = await fetch(`${API_BASE_URL}/portfolios/${selectedBookId}/transactions`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const dataTx = await resTx.json();
            if (dataTx.transactions) {
                setTransactions(dataTx.transactions);
            }

        } catch (e) {
            console.error("Failed to load details", e);
            toast.error("Could not load book details.");
        } finally {
            setIsDataRefreshing(false);
        }
    };

    // Trigger fetch when book changes
    useEffect(() => { 
        if(selectedBookId) fetchBookDetails(false); // False = Load Cached First
    }, [selectedBookId]);

    // --- ACTIONS ---

    const handleCreateBook = async () => {
        if (!newBookName.trim()) return;
        try {
            const token = await currentUser!.getIdToken();
            const res = await fetch(`${API_BASE_URL}/portfolios`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: newBookName, description: 'Created in AiQ' })
            });
            if (res.ok) {
                const newBook = await res.json();
                setBooks(prev => [...prev, newBook]);
                handleSelectBook(newBook.id);
                setShowCreateModal(false);
                setNewBookName('');
                toast.success("New Book Created");
            }
        } catch (e) { toast.error("Failed to create book"); }
    };

    const handleDeleteBook = async (bookId: string, e: React.MouseEvent) => {
        e.stopPropagation(); 
        if (!currentUser) return;
        if (!window.confirm("Delete this entire book and all positions? This cannot be undone.")) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/portfolios/${bookId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error("Delete failed");

            toast.success("Book deleted");
            setBooks(prev => prev.filter(b => b.id !== bookId));
            
            if (selectedBookId === bookId) {
                setSelectedBookId(null);
                localStorage.removeItem('aiq_last_book_id');
                setSummary(null);
            }
        } catch (e) {
            toast.error("Failed to delete book");
        }
    };

    const handleDeleteTransaction = async (txId: string) => {
        if (!selectedBookId || !currentUser) return;
        if (!window.confirm("Delete this trade? P&L will be recalculated.")) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/portfolios/${selectedBookId}/transactions/${txId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error("Delete failed");

            toast.success("Trade deleted");
            setTransactions(prev => prev.filter(tx => tx.id !== txId));
            
            // Trigger recalculation in background
            fetchBookDetails(true); 

        } catch (e) {
            toast.error("Failed to delete trade");
        }
    };

    const handleBookTrade = async () => {
        if (!selectedBookId) return;
        try {
            const token = await currentUser!.getIdToken();
            const res = await fetch(`${API_BASE_URL}/portfolios/${selectedBookId}/manual_trade`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ trade: tradeTicket })
            });

            if (!res.ok) throw new Error("Booking failed");

            toast.success("Trade Booked");
            setShowTradeModal(false);
            setTradeTicket({ ticker: '', action: 'BUY', amount: '', price: '' });

            // Trigger recalculation in background
            fetchBookDetails(true);

        } catch (e) { 
            toast.error("Failed to book trade"); 
        }
    };

    // --- HELPERS ---
    const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
    const formatPct = (val: number) => `${(val || 0).toFixed(2)}%`;
    const getPnlColor = (val: number) => val > 0 ? 'text-emerald-500' : val < 0 ? 'text-red-500' : 'text-slate-400';

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden font-sans">
            
            {/* --- LEFT SIDEBAR: BOOK LIST --- */}
            <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                    <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Book Master</h2>
                    <button onClick={() => setShowCreateModal(true)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500">
                        <IconPlus size={16} />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
                    {isBooksLoading && books.length === 0 ? (
                        <div className="p-4 text-xs text-slate-400 text-center animate-pulse">Loading Books...</div>
                    ) : (
                        books.map(book => (
                            <div 
                                key={book.id}
                                onClick={() => handleSelectBook(book.id)}
                                className={`relative group p-3 rounded-lg cursor-pointer transition-all border ${
                                    selectedBookId === book.id 
                                    ? 'bg-blue-50 border-blue-200 shadow-sm' 
                                    : 'bg-white border-transparent hover:bg-slate-50 text-slate-600'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1 pr-6">
                                    <IconBriefcase size={14} className={selectedBookId === book.id ? 'text-blue-600' : 'text-slate-400'} />
                                    <span className={`text-sm font-bold truncate ${selectedBookId === book.id ? 'text-blue-900' : 'text-slate-700'}`}>
                                        {book.name}
                                    </span>
                                </div>
                                <div className="text-[10px] text-slate-400 pl-6 truncate">
                                    {book.description || "No mandate"}
                                </div>

                                <button 
                                    onClick={(e) => handleDeleteBook(book.id, e)}
                                    className={`absolute top-2 right-2 p-1.5 rounded transition-all hover:bg-red-100 hover:text-red-600 ${selectedBookId === book.id ? 'text-slate-400' : 'text-slate-300 opacity-0 group-hover:opacity-100'}`}
                                    title="Delete Book"
                                >
                                    <IconTrash size={14} />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* --- MAIN CONTENT: BOOK DETAILS --- */}
            <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/50">
                {selectedBookId && summary ? (
                    <>
                        {/* KPI HEADER */}
                        <div className="h-20 bg-white border-b border-slate-200 flex items-center px-6 gap-8 shadow-sm shrink-0">
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Notional</div>
                                <div className="text-xl font-mono font-bold text-slate-800">{formatCurrency(summary.total_portfolio_value)}</div>
                            </div>
                            <div className="h-8 w-px bg-slate-100"></div>
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total P&L</div>
                                <div className={`text-xl font-mono font-bold ${getPnlColor(summary.total_pnl)}`}>
                                    {formatCurrency(summary.total_pnl)}
                                </div>
                            </div>
                            <div className="h-8 w-px bg-slate-100"></div>
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Daily P&L</div>
                                <div className={`text-xl font-mono font-bold ${getPnlColor(summary.total_daily_pnl)}`}>
                                    {formatCurrency(summary.total_daily_pnl)}
                                </div>
                            </div>
                            
                            <div className="ml-auto flex gap-3">
                                {/* MANUAL REFRESH / RECALCULATE BUTTON */}
                                <button 
                                    onClick={() => fetchBookDetails(true)} 
                                    disabled={isDataRefreshing}
                                    className={`p-2 rounded-full transition-all ${isDataRefreshing ? 'text-blue-600 bg-blue-50' : 'text-slate-400 hover:text-blue-600 hover:bg-slate-100'}`}
                                    title="Recalculate P&L"
                                >
                                    <IconRefresh className={isDataRefreshing ? 'animate-spin' : ''} size={18} />
                                </button>
                                <button 
                                    onClick={() => setShowTradeModal(true)}
                                    className="bg-slate-900 hover:bg-black text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shadow-md transition-all"
                                >
                                    <IconPlus size={14} /> Book Trade
                                </button>
                            </div>
                        </div>

                        {/* TABS - SEPARATED BUTTONS */}
                        <div className="px-6 pt-4 pb-2 bg-white border-b border-slate-200">
                            <div className="flex gap-4">
                                <button onClick={() => setActiveTab('POSITIONS')} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${activeTab === 'POSITIONS' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Overview (Positions)</button>
                                <button onClick={() => setActiveTab('TRANSACTIONS')} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${activeTab === 'TRANSACTIONS' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Blotter (Raw Trades)</button>
                            </div>
                        </div>

                        {/* CONTENT AREA */}
                        <div className="flex-1 p-6 overflow-auto custom-scrollbar relative">
                            {/* Overlay Spinner for Refreshing */}
                            {isDataRefreshing && (
                                <div className="absolute top-0 left-0 w-full h-1 bg-blue-100 overflow-hidden z-10">
                                    <div className="h-full bg-blue-500 animate-progress"></div>
                                </div>
                            )}

                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                {activeTab === 'POSITIONS' ? (
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                                            <tr>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider">Ticker</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Exposure</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Cost Basis</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Mark Price</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Mkt Value</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Unrealized P&L</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {positions.map((pos) => (
                                                <tr key={pos.ticker} className="hover:bg-blue-50/30 transition-colors group">
                                                    <td className="px-6 py-3"><span className="font-bold text-slate-800 text-xs bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">{pos.ticker}</span></td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs text-slate-600">{pos.shares.toFixed(4)}</td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs text-slate-500">{formatCurrency(pos.avg_cost)}</td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs font-medium text-slate-800">{formatCurrency(pos.current_price)}</td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs font-bold text-slate-800">{formatCurrency(pos.market_value)}</td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs font-medium">
                                                        <span className={getPnlColor(pos.total_pnl)}>{formatCurrency(pos.total_pnl)}</span>
                                                        <span className={`ml-2 text-[10px] ${getPnlColor(pos.pnl_percent)} bg-opacity-10 px-1 rounded`}>{pos.total_pnl > 0 ? '+' : ''}{formatPct(pos.pnl_percent)}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {positions.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400 text-sm">No active positions in this book.</td></tr>}
                                        </tbody>
                                    </table>
                                ) : (
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                                            <tr>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider">Ticker</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider">Side</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Qty</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Price</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-right">Total</th>
                                                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {transactions.map((tx) => (
                                                <tr key={tx.id} className="hover:bg-blue-50/30 transition-colors">
                                                    <td className="px-6 py-3 text-xs text-slate-500 font-mono">{new Date(tx.timestamp).toLocaleDateString()}</td>
                                                    <td className="px-6 py-3 font-bold text-xs text-slate-800">{tx.ticker}</td>
                                                    <td className="px-6 py-3"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${tx.action === 'BUY' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>{tx.action}</span></td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs">{tx.amount}</td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs">{formatCurrency(tx.price)}</td>
                                                    <td className="px-6 py-3 text-right font-mono text-xs font-bold text-slate-600">{formatCurrency(tx.amount * tx.price)}</td>
                                                    <td className="px-6 py-3 text-center">
                                                        <button onClick={() => handleDeleteTransaction(tx.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete Transaction"><IconTrash size={14} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {transactions.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">No transactions found.</td></tr>}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                        <IconBriefcase size={48} className="mb-4 opacity-20" />
                        <p className="text-sm">Select a Book to manage positions.</p>
                    </div>
                )}
            </div>

            {/* MODALS */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-2xl w-96 p-6 border border-slate-200">
                        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-800">New Book</h3><button onClick={() => setShowCreateModal(false)}><IconX size={18} className="text-slate-400 hover:text-slate-600"/></button></div>
                        <input className="w-full p-3 border border-slate-300 rounded-lg text-sm mb-4 focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Book Name" value={newBookName} onChange={e => setNewBookName(e.target.value)} autoFocus />
                        <button onClick={handleCreateBook} className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700">Create</button>
                    </div>
                </div>
            )}

            {showTradeModal && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-2xl w-96 p-6 border border-slate-200">
                        <div className="flex justify-between items-center mb-6"><h3 className="font-bold text-slate-800 flex items-center gap-2"><IconPlus size={16}/> Trade Ticket</h3><button onClick={() => setShowTradeModal(false)}><IconX size={18} className="text-slate-400 hover:text-slate-600"/></button></div>
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <button onClick={() => setTradeTicket({...tradeTicket, action: 'BUY'})} className={`flex-1 py-2 text-xs font-bold rounded border ${tradeTicket.action === 'BUY' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-200'}`}>BUY</button>
                                <button onClick={() => setTradeTicket({...tradeTicket, action: 'SELL'})} className={`flex-1 py-2 text-xs font-bold rounded border ${tradeTicket.action === 'SELL' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-500 border-slate-200'}`}>SELL</button>
                            </div>
                            <div><label className="text-[10px] font-bold text-slate-500 uppercase">Ticker</label><input className="w-full p-2 border border-slate-300 rounded text-sm font-mono uppercase focus:ring-1 focus:ring-blue-500 outline-none" value={tradeTicket.ticker} onChange={e => setTradeTicket({...tradeTicket, ticker: e.target.value.toUpperCase()})} placeholder="AAPL" /></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-[10px] font-bold text-slate-500 uppercase">Qty</label><input type="number" className="w-full p-2 border border-slate-300 rounded text-sm font-mono focus:ring-1 focus:ring-blue-500 outline-none" value={tradeTicket.amount} onChange={e => setTradeTicket({...tradeTicket, amount: e.target.value})} placeholder="0" /></div>
                                <div><label className="text-[10px] font-bold text-slate-500 uppercase">Price</label><input type="number" className="w-full p-2 border border-slate-300 rounded text-sm font-mono focus:ring-1 focus:ring-blue-500 outline-none" value={tradeTicket.price} onChange={e => setTradeTicket({...tradeTicket, price: e.target.value})} placeholder="0.00" /></div>
                            </div>
                            <button onClick={handleBookTrade} disabled={!tradeTicket.ticker || !tradeTicket.amount || !tradeTicket.price} className="w-full bg-slate-900 text-white py-3 rounded-lg text-sm font-bold hover:bg-black mt-2 disabled:opacity-50">Submit Trade</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioView;